import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IImage } from '../../../modules/reducers';
import {
  changeModalState,
  deleteImage,
  handlePhotoswipeAction,
  openImageInFullscreenAction,
  setImageTitle,
  setSmallScreenImageTitle
} from '../../../modules/actions';
import { getDeletionStatus, getUser, isRenderableImage } from '../../../modules/selectors';
import { useImageCommentCount, useOpenImageComments } from './useImageComments';
// `import type` matters here: the component imports this hook, so a value import
// would close a runtime cycle. esbuild drops `import type` per-file, TS elision
// alone is not guaranteed under isolated transpilation.
import type { ActionButtonsTypes } from '../ImageCardActions';

interface IParams {
  image: IImage;
  hideButtons: ActionButtonsTypes[];
  actionID?: string;
  galleriesImages: IImage[];
  isPhotoswipe?: boolean;
}

export const useImageCardActions = ({
  image,
  hideButtons,
  actionID,
  galleriesImages,
  isPhotoswipe
}: IParams) => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const isImageDeletionInProcess = useSelector(getDeletionStatus);

  const { isNewCommentsDesign, openImageComments } = useOpenImageComments();
  const commentCount = useImageCommentCount(image._id);
  // Only in the fullscreen lightbox - the grid tiles carry their own corner
  // badge, so showing it here too would double up on hover.
  const showComments = Boolean(isPhotoswipe && isNewCommentsDesign && commentCount > 0);

  // One object rather than nine separate strings, so the ids are recomputed as a
  // unit only when the image or the surface they belong to changes.
  const ids = useMemo(
    () => ({
      title: `${actionID}_title_${image._id}`,
      smallScreenTitle: `${actionID}_small_screen_title_${image._id}`,
      fullscreen: `${actionID}_fullscreen_${image._id}`,
      crop: `${actionID}_tcrop_${image._id}`,
      info: `${actionID}_tinfo_${image._id}`,
      download: `${actionID}_tdownload_${image._id}`,
      watermark: `${actionID}_twater_${image._id}`,
      delete: `${actionID}_tdelete_${image._id}`,
      comments: `${actionID}_tcomments_${image._id}`
    }),
    [actionID, image._id]
  );

  const downloadHref = useMemo(
    () =>
      `${window.API_URL_DL}/api/image/download/${image._collection}/${image._id}?q=high&authToken=${user.authKey}`,
    [image._collection, image._id, user.authKey]
  );

  const checkIsButtonHidden = useCallback(
    (buttonName: ActionButtonsTypes) => hideButtons?.includes(buttonName),
    [hideButtons]
  );

  const setTitleImageHandler = useCallback(() => dispatch(setImageTitle(image)), [dispatch, image]);

  const setSmallScreenTitleImageHandler = useCallback(
    () => dispatch(setSmallScreenImageTitle(image)),
    [dispatch, image]
  );

  const deleteImageHandler = useCallback(() => {
    if (isImageDeletionInProcess) return;

    dispatch(deleteImage(image._id));
  }, [dispatch, isImageDeletionInProcess, image._id]);

  const setWatermarkHandler = useCallback(() => {
    dispatch(
      changeModalState({
        key: 'selectWaterMarkModal',
        state: true,
        modalData: {
          watermarkType: 'image',
          targetID: image._id,
          watermarkID: image._watermark
        }
      })
    );
  }, [dispatch, image._id, image._watermark]);

  const cropImageHandler = useCallback(() => {
    dispatch(
      changeModalState({
        key: 'cropImageModal',
        state: true,
        modalData: { targetID: image._id }
      })
    );
  }, [dispatch, image._id]);

  const openCommentsHandler = useCallback(
    () => openImageComments(image._id),
    [openImageComments, image._id]
  );

  const onOpenInFullscreen = useCallback(() => {
    // keep the index aligned with the photoswipe items list (usePhotoswipeImages)
    const renderableImages = galleriesImages.filter(isRenderableImage);
    const currentIdx = renderableImages.findIndex(({ _id }) => _id === image._id);

    dispatch(openImageInFullscreenAction({ images: renderableImages, index: currentIdx }));
    dispatch(handlePhotoswipeAction({ image, index: currentIdx }));
  }, [dispatch, galleriesImages, image]);

  const preventPropagation = useCallback((e: any) => e.stopPropagation(), []);

  return {
    ids,
    downloadHref,
    commentCount,
    showComments,
    checkIsButtonHidden,
    setTitleImageHandler,
    setSmallScreenTitleImageHandler,
    deleteImageHandler,
    setWatermarkHandler,
    cropImageHandler,
    openCommentsHandler,
    onOpenInFullscreen,
    preventPropagation
  };
};
